/**
 * Trailblazers post-game statistics dashboard.
 *
 * Renders the server-built snapshot (AdminTrlResultsService) as a stack of
 * cards below the Results leaderboard. Every number here comes from that
 * snapshot — nothing is re-derived client-side, so the admin view and the
 * database can never disagree.
 */

import { useEffect } from 'react';
import type { AdminTrlResultsResponse } from '@swtorbit/shared-socket-events';
import { useGameContext } from '../../../contexts/GameContext';
import { useTrlContext } from '../../../contexts/TrlContext';
import { formatMs } from './statsFormat';
import { TrlPaceCard } from './TrlPaceCard';
import { TrlPlanetBreakdownCard } from './TrlPlanetBreakdownCard';
import { TrlSabotageCard } from './TrlSabotageCard';
import { TrlDisruptionCard } from './TrlDisruptionCard';
import { TrlScannerClueCard } from './TrlScannerClueCard';
import { TrlTreasureLedgerCard } from './TrlTreasureLedgerCard';
import { TrlTimelineCard } from './TrlTimelineCard';

/** The headline numbers, shown above the cards. */
function GameSummary({ results }: { results: AdminTrlResultsResponse }) {
  const cells: { label: string; value: string }[] = [
    { label: 'Duration', value: formatMs(results.durationMs) },
    { label: 'Teams', value: String(results.teams.length) },
    { label: 'Planets', value: String(results.planetCount) },
    { label: 'Finished', value: `${results.teams.filter((t) => t.finished).length} of ${results.teams.length}` },
  ];

  return (
    <div className="d-flex gap-sm flex-wrap">
      {cells.map((c) => (
        <div
          key={c.label}
          className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm flex-1 d-flex flex-column gap-2xs"
        >
          <span className="fs-xs text-slate">{c.label}</span>
          <span className="fs-lg text-white">{c.value}</span>
        </div>
      ))}
    </div>
  );
}

export function TrlStatsPanel() {
  const { selectedGame } = useGameContext();
  const { trlAdminResults, refreshTrlAdminResults } = useTrlContext();

  /*
   * Fetch once per selected game. Keyed on the room code rather than the game
   * object: `selectedGame` is replaced on every socket broadcast, so depending
   * on it would re-request the whole snapshot several times a second during a
   * live game.
   */
  useEffect(() => {
    if (selectedGame) refreshTrlAdminResults();
  }, [selectedGame?.dbId, refreshTrlAdminResults]);

  if (!trlAdminResults) {
    return (
      <div className="bg-space bg-o-50 text-cyan p-sm rounded-base border-md border-blue-light">
        Loading statistics…
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-sm">
      <h3 className="text-white">📊 Game Statistics</h3>

      <GameSummary results={trlAdminResults} />

      <TrlTimelineCard results={trlAdminResults} />
      <TrlPaceCard teams={trlAdminResults.teams} />
      <TrlPlanetBreakdownCard teams={trlAdminResults.teams} />
      <TrlTreasureLedgerCard treasure={trlAdminResults.treasure} />
      <TrlSabotageCard sabotage={trlAdminResults.sabotage} />
      <TrlDisruptionCard disruption={trlAdminResults.disruption} />
      <TrlScannerClueCard scanner={trlAdminResults.scanner} clues={trlAdminResults.clues} />
    </div>
  );
}
